
import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import { supabase } from "@/lib/supabase";
import { Plan, Subscription } from "./types";
import { toast } from "sonner";

interface SubscriptionContextType {
  isLoading: boolean;
  subscription: Subscription | null;
  isFreePlan: boolean;
  userPlan: Plan | null;
  planLimits: {
    maxChitFunds: number;
    maxMembers: number;
  };
  checkSubscription: () => Promise<void>;
}

const defaultSubscriptionContext: SubscriptionContextType = {
  isLoading: true,
  subscription: null,
  isFreePlan: true,
  userPlan: null,
  planLimits: {
    maxChitFunds: 1,
    maxMembers: 10
  },
  checkSubscription: async () => {}
};

const SubscriptionContext = createContext<SubscriptionContextType>(defaultSubscriptionContext);

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isFreePlan, setIsFreePlan] = useState(true);
  const [userPlan, setUserPlan] = useState<Plan | null>(null);
  
  const checkSubscription = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.error("Error checking subscription:", error);
        toast.error("Failed to check subscription status");
        return;
      }
      
      if (data.hasSubscription) {
        setIsFreePlan(data.isFreePlan);
        setUserPlan(data.plan);
        
        if (!data.isFreePlan) {
          setSubscription(data.subscription);
        } else {
          setSubscription(null);
        }
      } else {
        // Default to free plan if no subscription
        setIsFreePlan(true);
        setSubscription(null);
        
        // Get the free plan
        const { data: plansData, error: plansError } = await supabase
          .from('plans')
          .select('*')
          .eq('price', 0)
          .single();
        
        if (!plansError && plansData) {
          setUserPlan(plansData);
        }
      }
    } catch (error) {
      console.error("Error in checkSubscription:", error);
      toast.error("Failed to check subscription status");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkSubscription();
  }, [user]);

  const planLimits = {
    maxChitFunds: userPlan?.max_chit_funds || 1,
    maxMembers: userPlan?.max_members || 10
  };

  return (
    <SubscriptionContext.Provider
      value={{
        isLoading,
        subscription,
        isFreePlan,
        userPlan,
        planLimits,
        checkSubscription
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
